import { useState } from 'react';
import { writeBatch, doc, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import { logAdminAction } from '../../utils/auditLogger';
import { triggerWalletUpdate } from '../../firebase/functions/walletTriggers';
import { getAuth } from 'firebase/auth';

export default function BatchOperationsManager({ selectedItems, type, onComplete }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const auth = getAuth();

  const handleBatchOperation = async (operation) => {
    if (!window.confirm(t('common.confirmBatchOperation', { operation, count: selectedItems.length }))) {
      return;
    }

    setLoading(true);
    const batch = writeBatch(db);
    const updates = [];

    try {
      switch (operation) {
        case 'activate':
          selectedItems.forEach(item => {
            const ref = doc(db, type === 'users' ? 'walletUsers' : 'walletTransactions', item.id);
            const updateData = {
              'details.status': '1',
              last_updated: new Date()
            };
            batch.update(ref, updateData);
            updates.push({
              id: item.id,
              type: 'status_update',
              oldValue: item.details?.status,
              newValue: '1',
              timestamp: new Date().toISOString()
            });
          });
          break;

        case 'deactivate':
          selectedItems.forEach(item => {
            const ref = doc(db, type === 'users' ? 'walletUsers' : 'walletTransactions', item.id);
            const updateData = {
              'details.status': '0',
              last_updated: new Date()
            };
            batch.update(ref, updateData);
            updates.push({
              id: item.id,
              type: 'status_update',
              oldValue: item.details?.status,
              newValue: '0',
              timestamp: new Date().toISOString()
            });
          });
          break;

        case 'delete':
          selectedItems.forEach(item => {
            const ref = doc(db, type === 'users' ? 'walletUsers' : 'walletTransactions', item.id);
            const updateData = {
              deleted: true,
              deleted_at: new Date()
            };
            batch.update(ref, updateData);
            updates.push({
              id: item.id,
              type: 'delete',
              timestamp: new Date().toISOString()
            });
          });
          break;

        default:
          throw new Error('Invalid operation');
      }

      // Commit the batch
      await batch.commit();

      // Trigger wallet update
      await triggerWalletUpdate({
        operation,
        items: selectedItems.map(item => item.id),
        updates,
        type
      });

      // Log the batch operation with more details
      await logAdminAction(`${type}.batch_${operation}`, {
        itemCount: selectedItems.length,
        itemIds: selectedItems.map(item => item.id),
        operation,
        updates,
        timestamp: new Date().toISOString()
      });

      // Save to history collection
      const historyBatch = writeBatch(db);
      updates.forEach(update => {
        const historyRef = doc(collection(db, `${type}History`));
        historyBatch.set(historyRef, {
          ...update,
          operation,
          performedBy: auth.currentUser?.email,
          performedAt: new Date()
        });
      });
      await historyBatch.commit();

      toast.success(t('common.batchOperationSuccess', { count: selectedItems.length }));
      onComplete?.();
    } catch (error) {
      console.error('Error performing batch operation:', error);
      toast.error(t('common.batchOperationError'));
      
      // Log the error
      await logAdminAction(`${type}.batch_${operation}_error`, {
        error: error.message,
        itemCount: selectedItems.length,
        itemIds: selectedItems.map(item => item.id),
        operation
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow">
      <span className="text-sm text-gray-500">
        {selectedItems.length} {t('common.itemsSelected')}
      </span>
      
      <button
        onClick={() => handleBatchOperation('activate')}
        disabled={loading}
        className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-md"
      >
        <Icons.CheckCircle className="h-4 w-4 inline mr-1" />
        {t('common.activate')}
      </button>

      <button
        onClick={() => handleBatchOperation('deactivate')}
        disabled={loading}
        className="px-3 py-1 text-sm text-orange-600 hover:bg-orange-50 rounded-md"
      >
        <Icons.XCircle className="h-4 w-4 inline mr-1" />
        {t('common.deactivate')}
      </button>

      <button
        onClick={() => handleBatchOperation('delete')}
        disabled={loading}
        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md"
      >
        <Icons.Trash2 className="h-4 w-4 inline mr-1" />
        {t('common.delete')}
      </button>

      {loading && <Icons.Loader2 className="h-4 w-4 animate-spin text-gray-500" />}
    </div>
  );
} 